import React, { useState, useEffect } from 'react';
import { useUserStore } from '../stores/useUserStore';
import { useNotification } from '../hooks/useNotification';
import { useTranslation } from '../hooks/useTranslation';
import { Bonus, BonusCreateEntry } from '../services/bonusService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Clock, Save, X } from 'lucide-react';

interface BonusFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (bonus: Bonus) => void;
  bonus?: Bonus | null;
}

const ModernBonusForm: React.FC<BonusFormProps> = ({
  open,
  onClose,
  onSave,
  bonus,
}) => {
  const { user } = useUserStore();
  const { notify } = useNotification();
  const { translate, locale } = useTranslation();
  const [loading, setLoading] = useState(false);
  // UI state for form fields that use 'all' as a placeholder
  const [uiPosition, setUiPosition] = useState<string>('all');
  const [uiTimeOfDay, setUiTimeOfDay] = useState<string>('all');
  const [uiStaffingType, setUiStaffingType] = useState<string>('all');
  
  const [formData, setFormData] = useState<Partial<BonusCreateEntry>>({
    name: '',
    description: '',
    entityType: 'shift',
    position: undefined,
    staffingType: undefined,
    timeOfDay: undefined,
    customTimeRange: {
      startTime: '00:00',
      endTime: '23:59',
    },
    dayOfWeek: [],
    bonusAmount: 0,
    bonusCurrency: 'VND',
    isActive: true,
    createdBy: String(user?.id || ''),
    tenantId: (user as any)?.tenantId || '',
  });

  // Get entity types with translations - recalculate when translate function changes
  const entityTypes = React.useMemo(() => [
    { value: 'shift', label: translate('pos.bonuses.entity_type.shift', 'Shift') },
    { value: 'attendance', label: translate('pos.bonuses.entity_type.attendance', 'Attendance') },
    { value: 'performance', label: translate('pos.bonuses.entity_type.performance', 'Performance') },
    { value: 'other', label: translate('pos.bonuses.entity_type.other', 'Other') },
  ], [translate]);
  
  // Helper to get the current entity type label
  const getEntityTypeLabel = (value: string | undefined) => {
    const entityType = entityTypes.find(type => type.value === value);
    return entityType?.label || translate('pos.bonuses.entity_type.shift', 'Shift');
  };

  const positions = React.useMemo(() => [
    { value: 'all', label: translate('pos.bonuses.all_positions', 'All Positions') },
    { value: 'Barista', label: translate('pos.bonuses.position.barista', 'Barista') },
    { value: 'Cashier', label: translate('pos.bonuses.position.cashier', 'Cashier') },
    { value: 'Manager', label: translate('pos.bonuses.position.manager', 'Manager') },
    { value: 'Cook', label: translate('pos.bonuses.position.cook', 'Cook') },
    { value: 'Server', label: translate('pos.bonuses.position.server', 'Server') },
  ], [translate]);

  const staffingTypes = React.useMemo(() => [
    { value: 'all', label: translate('pos.bonuses.all_staffing_types', 'All Staffing Types') },
    { value: 'full-time', label: translate('pos.staff.profile.full_time', 'Full-Time') },
    { value: 'part-time', label: translate('pos.staff.profile.part_time', 'Part-Time') },
    { value: 'contract', label: translate('pos.staff.profile.contract', 'Contract') },
  ], [translate]);

  const timeOfDayOptions = React.useMemo(() => [
    { value: 'morning', label: translate('pos.bonuses.time_of_day.morning', 'Morning') },
    { value: 'afternoon', label: translate('pos.bonuses.time_of_day.afternoon', 'Afternoon') },
    { value: 'evening', label: translate('pos.bonuses.time_of_day.evening', 'Evening') },
    { value: 'night', label: translate('pos.bonuses.time_of_day.night', 'Night') },
    { value: 'custom', label: translate('pos.bonuses.time_of_day.custom', 'Custom Time Range') },
  ], [translate]);

  const dayOfWeekOptions = React.useMemo(() => [
    { value: 0, label: translate('pos.bonuses.day.sunday', 'Sunday') },
    { value: 1, label: translate('pos.bonuses.day.monday', 'Monday') },
    { value: 2, label: translate('pos.bonuses.day.tuesday', 'Tuesday') },
    { value: 3, label: translate('pos.bonuses.day.wednesday', 'Wednesday') },
    { value: 4, label: translate('pos.bonuses.day.thursday', 'Thursday') },
    { value: 5, label: translate('pos.bonuses.day.friday', 'Friday') },
    { value: 6, label: translate('pos.bonuses.day.saturday', 'Saturday') },
  ], [translate]);

  const currencyOptions = [
    { value: 'VND', label: 'VND' },
    { value: 'USD', label: 'USD' },
    { value: 'EUR', label: 'EUR' },
  ];

  useEffect(() => {
    if (open) {
      if (bonus) {
        const positionValue = bonus.position || 'all';
        const timeOfDayValue = bonus.timeOfDay || 'all';
        const staffingTypeValue = bonus.staffingType || 'all';
        setUiPosition(positionValue);
        setUiTimeOfDay(timeOfDayValue);
        setUiStaffingType(staffingTypeValue);
        setFormData({
          name: bonus.name,
          description: bonus.description || '',
          entityType: bonus.entityType,
          position: bonus.position || undefined,
          staffingType: bonus.staffingType || undefined,
          timeOfDay: bonus.timeOfDay || undefined,
          customTimeRange: bonus.customTimeRange || {
            startTime: '00:00',
            endTime: '23:59',
          },
          dayOfWeek: bonus.dayOfWeek || [],
          bonusAmount: bonus.bonusAmount,
          bonusCurrency: bonus.bonusCurrency,
          isActive: bonus.isActive,
          createdBy: String(bonus.createdBy),
          tenantId: bonus.tenantId,
        });
      } else {
        setUiPosition('all');
        setUiTimeOfDay('all');
        setUiStaffingType('all');
        setFormData({
          name: '',
          description: '',
          entityType: 'shift',
          position: undefined,
          staffingType: undefined,
          timeOfDay: undefined,
          customTimeRange: {
            startTime: '00:00',
            endTime: '23:59',
          },
          dayOfWeek: [],
          bonusAmount: 0,
          bonusCurrency: 'VND',
          isActive: true,
          createdBy: String(user?.id || ''),
          tenantId: (user as any)?.tenantId || '',
        });
      }
    }
  }, [open, bonus, user]);

  const handleSave = async () => {
    console.log('💾 Save button clicked');
    console.log('📋 Form data:', formData);
    console.log('👤 User:', user);
    console.log('🎯 UI Position:', uiPosition);
    console.log('⏰ UI TimeOfDay:', uiTimeOfDay);

    if (!formData.name || formData.bonusAmount === undefined || formData.bonusAmount <= 0) {
      console.warn('❌ Validation failed: name or bonusAmount missing');
      notify(
        translate('pos.bonuses.validation_error', 'Please fill in all required fields'),
        { type: 'error' }
      );
      return;
    }

    // Ensure we have user ID for createdBy
    const userId = formData.createdBy || user?.id;
    if (!userId) {
      console.warn('❌ Validation failed: user ID missing');
      notify(
        translate('pos.bonuses.user_required', 'User information is required. Please refresh the page.'),
        { type: 'error' }
      );
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Starting save process...');
      
      const bonusData: BonusCreateEntry = {
        name: formData.name!,
        description: formData.description,
        entityType: formData.entityType!,
        position: uiPosition === 'all' ? undefined : uiPosition,
        staffingType: uiStaffingType === 'all' ? undefined : (uiStaffingType as 'full-time' | 'part-time' | 'contract' | undefined),
        timeOfDay: uiTimeOfDay === 'all' ? undefined : (uiTimeOfDay as 'morning' | 'afternoon' | 'evening' | 'night' | 'custom' | undefined),
        customTimeRange: uiTimeOfDay === 'custom' ? formData.customTimeRange : undefined,
        dayOfWeek: formData.dayOfWeek || [],
        bonusAmount: formData.bonusAmount!,
        bonusCurrency: formData.bonusCurrency || 'VND',
        isActive: formData.isActive ?? true,
        createdBy: String(userId),
        // tenantId may be set by API automatically, but include it if available
        tenantId: formData.tenantId || (user as any)?.tenantId || '',
      };

      console.log('📤 Sending bonus data:', bonusData);

      let savedBonus: Bonus;
      const bonusServiceModule = await import('../services/bonusService');
      const bonusService = bonusServiceModule.default;
      
      if (bonus) {
        console.log('🔄 Updating existing bonus:', bonus.id);
        savedBonus = await bonusService.updateBonus(bonus.id, bonusData);
      } else {
        console.log('➕ Creating new bonus');
        savedBonus = await bonusService.createBonus(bonusData);
      }

      console.log('✅ Bonus saved successfully:', savedBonus);

      notify(
        translate(
          bonus
            ? 'pos.bonuses.update_success'
            : 'pos.bonuses.create_success',
          bonus
            ? 'Bonus updated successfully'
            : 'Bonus created successfully'
        ),
        { type: 'success' }
      );
      onSave(savedBonus);
      onClose();
    } catch (error: any) {
      console.error('❌ Error saving bonus:', error);
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        response: error?.response,
      });
      
      const errorMessage = error?.message || error?.response?.message || 
        (bonus ? 'Failed to update bonus' : 'Failed to create bonus');
      
      notify(
        translate(
          bonus
            ? 'pos.bonuses.update_error'
            : 'pos.bonuses.create_error',
          errorMessage
        ),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {bonus
              ? translate('pos.bonuses.edit', 'Edit Bonus')
              : translate('pos.bonuses.create', 'Create Bonus')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">
              {translate('pos.bonuses.name', 'Name')} *
            </Label>
            <Input
              id="name"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={translate('pos.bonuses.name_placeholder', 'e.g., Weekend Bonus')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              {translate('pos.bonuses.description', 'Description')}
            </Label>
            <Input
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={translate('pos.bonuses.description_placeholder', 'Optional description')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="entityType">
                {translate('pos.bonuses.entity_type', 'Entity Type')} *
              </Label>
              <Select
                key={`entity-type-${locale}`}
                value={formData.entityType || 'shift'}
                onValueChange={(value) =>
                  setFormData({ ...formData, entityType: value as any })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder={translate('pos.bonuses.entity_type', 'Entity Type')} />
                </SelectTrigger>
                <SelectContent>
                  {entityTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="position">
                {translate('pos.bonuses.position', 'Position')}
              </Label>
              <Select
                key={`position-${locale}`}
                value={uiPosition}
                onValueChange={(value) => {
                  setUiPosition(value);
                  setFormData({ ...formData, position: value === 'all' ? undefined : value });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {positions.map((pos) => (
                    <SelectItem key={pos.value} value={pos.value}>
                      {pos.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="staffingType">
                {translate('pos.bonuses.staffing_type', 'Staffing Type')}
              </Label>
              <Select
                key={`staffing-type-${locale}`}
                value={uiStaffingType}
                onValueChange={(value) => {
                  setUiStaffingType(value);
                  setFormData({ ...formData, staffingType: value === 'all' ? undefined : (value as 'full-time' | 'part-time' | 'contract' | undefined) });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={translate('pos.bonuses.staffing_type', 'Staffing Type')} />
                </SelectTrigger>
                <SelectContent>
                  {staffingTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.entityType === 'shift' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="timeOfDay">
                  {translate('pos.bonuses.time_of_day', 'Time of Day')}
                </Label>
                <Select
                  key={`time-of-day-${locale}`}
                  value={uiTimeOfDay}
                  onValueChange={(value) => {
                    setUiTimeOfDay(value);
                    setFormData({ 
                      ...formData, 
                      timeOfDay: value === 'all' ? undefined : (value as 'morning' | 'afternoon' | 'evening' | 'night' | 'custom' | undefined)
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {translate('pos.bonuses.any_time', 'Any Time')}
                    </SelectItem>
                    {timeOfDayOptions.map((time) => (
                      <SelectItem key={time.value} value={time.value}>
                        {time.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {uiTimeOfDay === 'custom' && (
                <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <Label className="font-medium">
                      {translate('pos.bonuses.custom_time_range', 'Custom Time Range')}
                    </Label>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="startTime">
                        {translate('pos.bonuses.start_time', 'Start Time')}
                      </Label>
                      <Input
                        id="startTime"
                        type="time"
                        value={formData.customTimeRange?.startTime || '00:00'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            customTimeRange: {
                              ...formData.customTimeRange!,
                              startTime: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endTime">
                        {translate('pos.bonuses.end_time', 'End Time')}
                      </Label>
                      <Input
                        id="endTime"
                        type="time"
                        value={formData.customTimeRange?.endTime || '23:59'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            customTimeRange: {
                              ...formData.customTimeRange!,
                              endTime: e.target.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>
                  {translate('pos.bonuses.days_of_week', 'Days of Week')}
                </Label>
                <div className="flex flex-wrap gap-2" key={`days-container-${locale}`}>
                  {dayOfWeekOptions.map((day) => (
                    <Button
                      key={`day-${day.value}-${locale}`}
                      type="button"
                      variant={
                        formData.dayOfWeek?.includes(day.value) ? 'default' : 'outline'
                      }
                      size="sm"
                      onClick={() => {
                        const currentDays = formData.dayOfWeek || [];
                        const newDays = currentDays.includes(day.value)
                          ? currentDays.filter((d) => d !== day.value)
                          : [...currentDays, day.value];
                        setFormData({ ...formData, dayOfWeek: newDays });
                      }}
                    >
                      {day.label}
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bonusAmount">
                {translate('pos.bonuses.bonus_amount', 'Bonus Amount')} *
              </Label>
              <Input
                id="bonusAmount"
                type="number"
                min="0"
                step="0.01"
                value={formData.bonusAmount || 0}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    bonusAmount: parseFloat(e.target.value) || 0,
                  })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bonusCurrency">
                {translate('pos.bonuses.currency', 'Currency')}
              </Label>
              <Select
                value={formData.bonusCurrency || 'VND'}
                onValueChange={(value) =>
                  setFormData({ ...formData, bonusCurrency: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencyOptions.map((curr) => (
                    <SelectItem key={curr.value} value={curr.value}>
                      {curr.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={formData.isActive ?? true}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, isActive: checked })
              }
            />
            <Label htmlFor="isActive">
              {translate('pos.bonuses.is_active', 'Active')}
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            <X className="h-4 w-4 mr-2" />
            {translate('pos.bonuses.cancel', 'Cancel')}
          </Button>
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('🔘 Save button clicked, calling handleSave');
              handleSave();
            }} 
            disabled={loading}
          >
            <Save className="h-4 w-4 mr-2" />
            {loading
              ? translate('pos.bonuses.saving', 'Saving...')
              : translate('pos.bonuses.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModernBonusForm;

